import 'leaflet/dist/leaflet.css';

import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { endOfWeek, startOfWeek } from 'date-fns';
import { format } from 'date-fns-tz';
import { saveAs } from 'file-saver';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { PlanningMapUnallocatedOrderPositionEntity } from 'src/entities/import/PlanningMapUnallocatedOrderPosition.entity';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { PlanningEntity } from 'src/entities/planningEntity';
import { AsyncPlanningResponseEntity, PlanningResponseEntity, PlanningResponseV5Entity } from 'src/entities/planningResponseEntity';
import {
  PlanningsDeliveryEntity,
  PlanningsDeliveryWithLegRunEntity,
} from 'src/entities/PlanningsDelivery.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { PlanningsMapGaragePositionEntity } from 'src/entities/PlanningsMapGaragePosition.entity';
import { PlanningsMapOperationPositionEntity } from 'src/entities/PlanningsMapOperationPosition.entity';
import { PlanningsOperationDeliveryByDeliveryIdEntity } from 'src/entities/PlanningsOperationEntitiesWithStatsByDeliveryId.entity';
import { PositionEntity } from 'src/entities/PositionEntity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { TransferRequestEntity } from 'src/entities/transferRequestEntity';
import { useGaragesQuery } from 'src/hooks/useGarages.query';
import { usePartnerCompaniesRequest } from 'src/hooks/usePartnerCompanies.request';
import { usePastOperationsQueryV5 } from 'src/hooks/usePastOperations.request.query';
import { usePlanningRequest } from 'src/hooks/usePlanningRequest';
import { useMutationShiftClone } from 'src/hooks/useQueryDriverShifts';
import { useQueryLastOperationStatusUpdated } from 'src/hooks/useQueryLastOperationStatusUpdated';
import { useQueryLatestAlgorithmRequestVersion } from 'src/hooks/useQueryLatestAlgorithmRequestVersion';
import { useMutationPlanningV5 } from 'src/hooks/useQueryPlanning';
import { useQueryPlanningGroups } from 'src/hooks/useQueryPlanningGroups';
import { useQueryPlanningsLegRunStatistics } from 'src/hooks/useQueryPlanningOrderStatistics';
import { PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY, useInfiniteQueryPlanningsDeliveriesWithLegRuns, useMutationPlanningDelivery } from 'src/hooks/useQueryPlanningsDeliveries';
import { useQueryPlanningsDrivers } from 'src/hooks/useQueryPlanningsDrivers';
import { useQueryPlanningsNotAllocReasons } from 'src/hooks/useQueryPlanningsNotAllocReasons';
import { useMutationPlanningOperations } from 'src/hooks/useQueryPlanningsOperations';
import { useMutationPlanningRunnings, useQueryPlanningsRunnings } from 'src/hooks/useQueryPlanningsRunnings';
import { useQueryPlanningsSuggestions } from 'src/hooks/useQueryPlanningsSuggestions';
import { useQueryPlanningsTrucks } from 'src/hooks/useQueryPlanningsTrucks';
import { useMutationShiftTruckFixedStatusV5 } from 'src/hooks/useQueryShiftTruckFixedStatus';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY, useInfiniteQueryShipmentOrderLegRuns } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { SHIPMENT_ORDERS_QUERY_KEY, useMutationShipmentOrder } from 'src/hooks/useQueryShipmentOrders';
import { useRollbackRequest } from 'src/hooks/useRollbackRequest';
import { useScenarioPlanningRequestV5 } from 'src/hooks/useScenarioPlanningRequestV5';
import { useMutationShipmentOrderLegRun } from 'src/hooks/useShipmentOrderLegRun';
import { useTariffCalculationMutation } from 'src/hooks/useTariffCalculation.mutation';
import { Operations } from 'src/models/Operations';
import { PlanningsOperationDelivery, PlanningsOperationPlace } from 'src/models/PlanningsOperationGroup.model';
import arrayUtil from 'src/utils/array.util';
import { convertShipmentOrderLegRunEntitiesToDeliveries } from 'src/utils/planningsDelivery.util';
import { PlanningViewKind } from 'src/vo/PlanningViewKind.vo';
import { RelaxedRuleValues } from 'src/vo/RelaxedRule.vo';

import { usePlanningQueue } from './hooks/usePlanningQueue';
import { usePlanningSettings } from './hooks/usePlanningSettings';
import Presenter from './Presenter';
import { TariffCalculationParams } from './presenters/TariffCalculationDialog';
import { currentHistoryVersionReducer,
  displayOrderIdReducer, editPlacesReducer, fitBoundsPositionsReducer,
  flyToPositionReducer,
  groupEntitiesReducer,
  importFileReducer,
  importForUpdateFileReducer,
  lastOperationStatusUpdatedAtReducer,
  operationEntitiesReducer,
  planningsOperationEntitiesWithStatsByDeliveryIdEntityReducer,
  shipmentOrderLegRunEntityMapReducer,
  transferRequestReducer
} from './reducers';

type Props = {
  startOn: string;
  endOn: string;
  unit: string;
  customInputFields: string[];
  activateConstraintLoadAfterNotCleared: boolean;
  loadAfterInitialStock: boolean;
}

const Component: FC<Props> = memo((
  {
    startOn,
    endOn,
    unit,
    customInputFields,
    activateConstraintLoadAfterNotCleared,
    loadAfterInitialStock,
  }
) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);
  const {
    deliveryEntities, selectedCycleIndexes, latestAlgorithmRequestVersion,
    planningsOperationDeliveryByDeliveryIdEntity, orderIds, selectedOrderIds, allocatedOrderIds,
    driverEntities, truckEntities, isLoading, notAllocState, generateRandomString, calculating
  } = plansContext;

  const DELIVERIES_PAR_PAGE = 10;
  const SHIPMENT_ORDER_LEG_RUNS_PER_PAGE = 200;

  useEffect(() => {
    plansContext.setStartOn(startOn);
    plansContext.setEndOn(endOn);
    licenseContext.setStartEndOn({ startOn, endOn });
  }, [endOn, startOn]);

  const reset = useCallback(() => {
    dispatchPlanning({
      type: 'updateStartOn',
      payload: startOn
    });
    dispatchPlanning({
      type: 'updateEndOn',
      payload: endOn
    });
    plansContext.resetOrderIds();
    plansContext.resetAllocatedOrderIds();
    plansContext.resetSelectedOrderIds();
    dispatchTransfer({ type: 'reset' });
    dispatchImportFile({ type: 'reset' });
    plansContext.resetCycleIndexes();
    plansContext.resetSelectedGroupEntities();
    setAllDeliveriesLoaded(false);

    queryClient.clear();
  }, [startOn, endOn]);

  // queryの前にないと、query が2回呼ばれてしまう。
  useEffect(() => {
    if (!licenseContext.config?.selected_company_id) return;
    reset();
  }, [startOn, endOn, licenseContext.config?.selected_company_id, reset]);

  const [view, setView] = useState<PlanningViewKind>('map');

  const {
    data: shipmentOrderLegRunsInfiniteData,
    isLoading: shipmentOrderLegRunsIsLoading,
    isFetching: shipmentOrderLegRunsIsFetching,
    hasNextPage: shipmentOrderLegRunsHasNextPage,
    fetchNextPage: fetchNextShipmentOrderLegRuns,
    isInitialLoading: shipmentOrderLegRunsIsInitialLoading,
  } = useInfiniteQueryShipmentOrderLegRuns(startOn, endOn, SHIPMENT_ORDER_LEG_RUNS_PER_PAGE);

  const { data: garageData } = useGaragesQuery();
  const { data: partnerCompanyData } = usePartnerCompaniesRequest(0, '', '', true, 5000);
  const { data: truckData, isLoading: trucksIsLoading } = useQueryPlanningsTrucks(startOn, endOn);
  const { data: driverData, isLoading: driversIsLoading } = useQueryPlanningsDrivers(startOn, endOn);
  const {
    data: deliveriesInfiniteData,
    isLoading: deliveriesIsLoading,
    isFetching: deliveriesIsFetching,
    hasNextPage: deliveriesHasNextPage,
    fetchNextPage: fetchNextDeliveries,
    isInitialLoading: deliveriesIsInitialLoading,
  } = useInfiniteQueryPlanningsDeliveriesWithLegRuns(startOn, endOn, DELIVERIES_PAR_PAGE);
  const [deliveryData, setDeliveryData] = useState<PlanningsDeliveryWithLegRunEntity[]>([]);
  // const { data: planningMapUnallocatedOrderPositionsData } = useQueryPlanningMapUnallocatedOrderPositions(startOn, endOn);
  const { data: planningGroupsData } = useQueryPlanningGroups();
  const { data: planningRunningEntities } = useQueryPlanningsRunnings(startOn, endOn);
  const relaxedRules = useMemo(() => RelaxedRuleValues.map((it) => it), []);

  const [shipmentOrderLegRunEntityMap, dispatchShipmentOrderLegRunEntityMap] = useReducer(shipmentOrderLegRunEntityMapReducer, null);
  const [operationEntities, dispatchOperationEntities] = useReducer(operationEntitiesReducer, []);
  const [groupEntities, dispatchGroupEntities] = useReducer(groupEntitiesReducer, []);
  const [partnerCompanyEntities, setPartnerCompanyEntities] = useState<PartnerCompanyEntity[]>([]);
  const [planningsOperationEntitiesWithStatsByDeliveryIdEntity, dispatchPlanningsOperationEntitiesWithStatsByDeliveryIdEntity] = useReducer(planningsOperationEntitiesWithStatsByDeliveryIdEntityReducer, {});
  const [editPlaces, dispatchEditPlaces] = useReducer(editPlacesReducer, []);
  const [lastOperationStatusUpdated, dispatchLastOperationStatusUpdated] = useReducer(lastOperationStatusUpdatedAtReducer, null);
  const { data: lastOperationStatusUpdatedData } = useQueryLastOperationStatusUpdated(startOn, endOn, deliveriesHasNextPage ? [] : deliveryEntities.map((it) => it.id));
  const { data: latestAlgorithmRequestVersionData } = useQueryLatestAlgorithmRequestVersion(startOn, endOn);
  const [flyToPosition, dispatchFlyToPosition] = useReducer(flyToPositionReducer, undefined);
  const [fitBoundsPositions, dispatchFitBoundsPositions] = useReducer(fitBoundsPositionsReducer, []);
  const [currentHistoryVersion, dispatchCurrentHistoryVersion] = useReducer(currentHistoryVersionReducer, undefined);
  const [latestAllocateHistory, setLatestAllocateHistory] = useState<AllocateHistoryEntity | undefined>(undefined);
  const [directionSettingDialogIsOpen, setDirectionSettingDialogIsOpen] = useState<boolean>(false);
  const [allDeliveriesLoaded, setAllDeliveriesLoaded] = useState<boolean>(false);
  const updateDirectionSettingDialogIsOpen = useCallback((val: boolean) => {
    setDirectionSettingDialogIsOpen(val);
  }, []);

  const { data: pastOperationsData } = usePastOperationsQueryV5(startOn);

  useEffect(() => {
    if (!partnerCompanyData) return;

    setPartnerCompanyEntities(partnerCompanyData.data);
  }, [partnerCompanyData]);

  useEffect(() => {
    if (allDeliveriesLoaded) return;
    if (!deliveriesInfiniteData || !deliveriesInfiniteData.pages) return;

    setAllDeliveriesLoaded(
      deliveriesInfiniteData.pages[0].total_pages_count === deliveriesInfiniteData.pages.length
      && deliveriesInfiniteData.pages.flatMap((it) => it.ids).length === deliveryEntities.length
    );
  }, [allDeliveriesLoaded, deliveriesInfiniteData, deliveryEntities]);

  const updateView = useCallback((val: 'map' | 'list') => {
    setView(val);
  }, []);

  const setCurrentHistoryVersion = useCallback((version: string) => {
    dispatchCurrentHistoryVersion({
      type: 'set',
      payload: version,
    });
  }, []);

  const resetAllocateHistories = useCallback(() => {
    plansContext.resetAllocateHistoryEntity();
  }, []);


  const resetCurrentHistoryVersion = useCallback(() => {
    dispatchCurrentHistoryVersion({
      type: 'reset'
    });

    plansContext.setDeliveryEntities(convertShipmentOrderLegRunEntitiesToDeliveries(deliveryData, shipmentOrderLegRunEntityMap) || []);
  }, [deliveryData, shipmentOrderLegRunEntityMap]);

  useEffect(() => {
    if (!currentHistoryVersion) return;

    const historyVersionDeliveries = plansContext.allocateHistories?.find(({ version }) => version === currentHistoryVersion)
      ?.deliveryEntities;

    if (!historyVersionDeliveries) return;

    plansContext.setDeliveryEntities(historyVersionDeliveries);
  }, [plansContext.allocateHistories, currentHistoryVersion]);

  const resetFitBoundsPositions = useCallback(() => {
    dispatchFitBoundsPositions({
      type: 'reset'
    });
  }, []);

  const resetFlyToPosition = useCallback(() => {
    dispatchFlyToPosition({ type: 'reset' });
  }, []);

  const addEmptyCycle = useCallback((deliveryId: number) => {
    plansContext.addEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity(deliveryId);
  }, []);

  const removeEmptyCycle = useCallback((deliveryId: number, cycleIndex: number) => {
    plansContext.removeEmptyCyclePlanningsOperationDeliveryByDeliveryIdEntity(deliveryId, cycleIndex);
  }, []);

  const updateEditPlaces = useCallback((deliveryId: number, cycleIndex: number, places: PlanningsOperationPlace[]) => {
    dispatchEditPlaces({
      type: 'set',
      payload: {
        deliveryId,
        cycleIndex,
        places,
      }
    });
  }, []);

  const resetEditPlaces = useCallback(() => {
    dispatchEditPlaces({
      type: 'reset',
    });
  }, []);

  useEffect(() => {
    if (!operationEntities) return;
    if (!licenseContext.config) return;

    const model = new Operations(licenseContext.config, operationEntities);

    dispatchPlanningsOperationEntitiesWithStatsByDeliveryIdEntity({
      type: 'set',
      payload: model.groupAndSortEntitiesByDeliveryIdWithVolumeAndWeightIdx()
    });

    const tmp = model.createPlanningsOperationDeliveryByDeliveryIdEntity();
    const entity: PlanningsOperationDeliveryByDeliveryIdEntity = deliveryEntities.reduce((acc, delivery) => {
      acc[delivery.id] = tmp[delivery.id] ?? PlanningsOperationDelivery.empty(licenseContext.config?.delivery_list_grouping);
      return acc;
    }, {} as PlanningsOperationDeliveryByDeliveryIdEntity);

    plansContext.setPlanningsOperationDeliveryByDeliveryIdEntity(entity);
  }, [operationEntities, licenseContext.config, deliveryEntities]);

  const planningsMapGaragePositionEntities = useMemo((): PlanningsMapGaragePositionEntity[] => {
    if (!deliveryEntities || !garageData) return [];

    const garageMap = new Map<string, PlanningsMapGaragePositionEntity>();

    for (const delivery of deliveryEntities) {
      const garageKey = `${delivery.garageLatitude},${delivery.garageLongitude}`;
      let garagePosition = garageMap.get(garageKey);

      if (!garagePosition) {
        const garage = garageData.find((it) => it.latitude === delivery.garageLatitude && it.longitude === delivery.garageLongitude);
        garagePosition = {
          garageEntity: garage,
          position: [parseFloat(delivery.garageLatitude), parseFloat(delivery.garageLongitude)],
          deliveryIds: []
        };
        garageMap.set(garageKey, garagePosition);
      }

      garagePosition.deliveryIds.push(delivery.id);
    }

    return Array.from(garageMap.values());
  }, [deliveryEntities, garageData]);

  const planningsMapOperationPositionEntities = useMemo((): PlanningsMapOperationPositionEntity[] => {
    if (!operationEntities) return [];

    const deliveryMap = new Map<string, PlanningsMapOperationPositionEntity>();

    for (const operation of operationEntities) {
      const garageKey = `${operation.latitude},${operation.longitude}`;
      let garagePosition = deliveryMap.get(garageKey);

      if (!garagePosition) {
        garagePosition = {
          position: [parseFloat(operation.latitude), parseFloat(operation.longitude)] as PositionEntity,
          deliveryIds: []
        };
        deliveryMap.set(garageKey, garagePosition);
      }

      garagePosition.deliveryIds.push(operation.shiftTruckId);
    }

    return Array.from(deliveryMap.values());
  }, [operationEntities]);

  useEffect(() => {
    if (!planningGroupsData || !truckData || !deliveryData
      || truckData.length === 0 || deliveryData.length === 0
      || deliveriesHasNextPage) return;

    const allTrucks: PlanningsGroupEntity = {
      id: 0,
      name: 'すべて表示する',
      truckIds: truckData.map((it) => it.id),
    };
    const deliveryTruckIds = deliveryData.map((it) => it.truckId);
    const filteredGroupData = planningGroupsData.filter((it) => it.truckIds.some((id) => deliveryTruckIds.includes(id)));
    dispatchGroupEntities({
      type: 'set',
      payload: [allTrucks, ...filteredGroupData]
    });
  }, [deliveryData, planningGroupsData, truckData, deliveriesHasNextPage]);

  const planningMapUnallocatedOrderPositionEntities = useMemo((): PlanningMapUnallocatedOrderPositionEntity[] => {
    if (!shipmentOrderLegRunsInfiniteData) return [];

    const map = new Map<string, number[]>();
    shipmentOrderLegRunsInfiniteData.pages.forEach((page) => {
      page.data.forEach((order) => {
        if (order.assigned) return;

        const loadingLat = Number(order.loading_lat);
        const loadingLng = Number(order.loading_lng);
        if (!Number.isFinite(loadingLat) || !Number.isFinite(loadingLng)) {
          console.warn('未割当案件の積地座標が不正なためスキップします', { id: order.id, loading_lat: order.loading_lat, loading_lng: order.loading_lng });
          return;
        }
        const loadingPosition: PositionEntity = [loadingLat, loadingLng];
        const loadingOrderIds = map.get(loadingPosition.join('_')) || [];
        loadingOrderIds.push(order.id);
        map.set(loadingPosition.join('_'), loadingOrderIds);

        const unloadingLat = Number(order.unloading_lat);
        const unloadingLng = Number(order.unloading_lng);
        if (!Number.isFinite(unloadingLat) || !Number.isFinite(unloadingLng)) {
          console.warn('未割当案件の納品地座標が不正なためスキップします', { id: order.id, unloading_lat: order.unloading_lat, unloading_lng: order.unloading_lng });
          return;
        }
        const unloadingPosition: PositionEntity = [unloadingLat, unloadingLng];
        const unloadingOrderIds = map.get(unloadingPosition.join('_')) || [];
        unloadingOrderIds.push(order.id);
        map.set(unloadingPosition.join('_'), unloadingOrderIds);
      });
    });

    return Array.from(map.entries()).map(([position, positionOrderIds]) => ({
      position: position.split('_').map((it) => Number(it)) as PositionEntity,
      ids: positionOrderIds,
    }));
  }, [shipmentOrderLegRunsInfiniteData]);

  const [displayOrderId, dispatchDisplayOrderId] = useReducer(displayOrderIdReducer, undefined);
  const updateDisplayOrderId: (orderId: number) => void = useCallback(
    (orderId) => {
      dispatchDisplayOrderId({
        type: 'update',
        payload: orderId
      });
    },
    [],
  );

  const resetDisplayOrderId: () => void = useCallback(
    () => {
      dispatchDisplayOrderId({
        type: 'reset'
      });
    },
    [],
  );

  const { normalPlanning, transferPlanning, swapPlanning, assignTruckToTermporaryDelivery, operationBulkResetting } = useMutationPlanningV5();
  const { restoreSplitLegRun, restoreSplitLegRuns } = useMutationShipmentOrderLegRun();
  const { bulkDelete } = useMutationShipmentOrder();
  const bulkDeleteMutate = bulkDelete.mutate;
  const { cloneShipmentOrder } = useMutationShipmentOrder();
  const { delete: deletePlanningRunning } = useMutationPlanningRunnings();
  const confirmMutation = useMutationShiftTruckFixedStatusV5();

  const updateSelectedCycleIndexes = useCallback((deliveryId: number, cycleIndexes: number[]) => {
    if (cycleIndexes.length === 0) {
      plansContext.removeCycleIndex(deliveryId);
      return;
    }
    plansContext.updateCycleIndex({ deliveryId, cycleIndexes });
  }, []);

  const childRef = useRef<{ afterResetQuery:() => void; } | null>(null);
  const resetQueueRef = useRef<() => void>(() => {});

  const resetQuery = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY]
    });
    void queryClient.invalidateQueries({
      queryKey: ['useQueryPlanningsLegRunStatistics']
    });
    void queryClient.invalidateQueries({
      queryKey: ['planningMapUnallocatedOrderPositions', { startOn, endOn }]
    });
    void queryClient.invalidateQueries({
      queryKey: ['planningMapPositions', { startOn, endOn }]
    });
    void queryClient.invalidateQueries({
      queryKey: ['useDeliveriesQuery']
    });
    void queryClient.invalidateQueries({
      queryKey: ['useQueryPlanningsNotAllocReasons']
    });
    void queryClient.invalidateQueries({
      queryKey: ['useQueryLatestAlgorithmRequestVersion']
    });
    void queryClient.invalidateQueries({
      queryKey: ['useQueryPlanningScenario']
    });
    void queryClient.invalidateQueries({
      queryKey: ['useQueryPlanningSettings']
    });
    void queryClient.invalidateQueries({
      queryKey: ['useOrdersRequest']
    });
    void queryClient.invalidateQueries({
      queryKey: ['useQueryPlanningsDrivers']
    });
    void queryClient.invalidateQueries({
      queryKey: ['useQueryPlanningsTrucks']
    });
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY]});
    void queryClient.invalidateQueries({ queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY]});
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDERS_QUERY_KEY]});

    resetQueueRef.current();

    if (childRef.current) {
      childRef.current.afterResetQuery();
    }
  }, [endOn, queryClient, startOn]);

  const afterResetOrders = () => {
    plansContext.resetAllocatedOrderIds();
    resetQuery();
  };

  const refreshRunningEntities = useCallback(() => {
    plansContext.refreshRunningEntities();
  }, []);

  const cancelRunningPlan = useCallback((id: number) => {
    deletePlanningRunning.mutate({ id, startOn, endOn }, {
      onSuccess: () => {
        enqueueSnackbar('実行中の自動配車を停止しました。');
      },
      onError: (_) => {
        enqueueSnackbar('自動配車の停止処理でエラーが発生しました。');
      },
      onSettled: () => {
        setAsyncPlanningResponse(null);
        queryClient.clear();
        plansContext.setIsLoading(false);
        plansContext.setCalculating(false);
        resetQueueRef.current();
        plansContext.resetAllocatedOrderIds();
      }
    });
  }, [deletePlanningRunning, endOn, enqueueSnackbar, queryClient, startOn]);

  const addSelectedOrderId = useCallback(
    (id: number) => {
      plansContext.addSelectedOrderIds(id);
    },
    [],
  );
  const removeSelectedOrderId = useCallback(
    (id: number) => {
      plansContext.removeSelectedOrderIds(id);
    },
    [],
  );

  const mutateRestoreSplittedOrder: (orderId: number) => void = useCallback((orderId) => {
    if (!window.confirm('分割した案件を復元します。よろしいですか？')) return;

    plansContext.setIsLoading(true);
    restoreSplitLegRun.mutate(
      orderId,
      {
        onSuccess: () => {
          resetQuery();
          enqueueSnackbar('分割した案件を元に戻しました。');
        },
        onError: () => {
          enqueueSnackbar('分割した案件を元に戻す処理でエラーが発生しました。');
        },
        onSettled: () => {
          plansContext.setIsLoading(false);
        }
      }
    );
  }, [resetQuery]);

  const mutateRestoreSplittedOrders: () => void = useCallback(() => {
    if (!window.confirm('分割した案件をすべて復元します。よろしいですか？')) return;

    plansContext.setIsLoading(true);
    restoreSplitLegRuns.mutate(
      { startOn, endOn },
      {
        onSuccess: () => {
          resetQuery();
          enqueueSnackbar('分割した案件を元に戻しました。');
        },
        onError: () => {
          enqueueSnackbar('分割した案件を元に戻す処理でエラーが発生しました。');
        },
        onSettled: () => {
          plansContext.setIsLoading(false);
        }
      }
    );
  }, [endOn, resetQuery, startOn]);

  const mutateDeleteOrder: () => void = useCallback(() => {
    if (!selectedOrderIds) return;
    if (!selectedOrderIds.length) return;

    enqueueSnackbar(`${selectedOrderIds.length.toLocaleString()}件の案件を削除します`);
    plansContext.setIsLoading(true);

    const ids = selectedOrderIds.map((id) => shipmentOrderLegRunEntityMap.get(id)).map((it) => it.shipment_order_id);
    plansContext.bulkRemoveSelectedOrderIds(selectedOrderIds);
    plansContext.removeOrderIds(selectedOrderIds);
    plansContext.addAllocatedOrderIds(selectedOrderIds);

    bulkDelete.mutate(
      ids,
      {
        onSuccess: () => {
          resetQuery();
          enqueueSnackbar(`${selectedOrderIds.length.toLocaleString()}件の案件を削除しました`, { variant: 'success' });
        },
        onError: () => {
          plansContext.bulkAddSelectedOrderIds(selectedOrderIds);
          plansContext.addOrderIds(selectedOrderIds);
          plansContext.removeAllocatedOrderIds(selectedOrderIds);
          enqueueSnackbar('案件の削除でエラーが発生しました。', { variant: 'error' });
        },
        onSettled: () => {
          plansContext.setIsLoading(false);
        }
      }
    );
  }, [enqueueSnackbar, resetQuery, selectedOrderIds, shipmentOrderLegRunEntityMap]);

  const mutateDeleteSpecificOrder: (orderId: number) => void = useCallback((orderId) => {
    plansContext.setIsLoading(true);

    const id = shipmentOrderLegRunEntityMap.get(orderId).shipment_order_id;
    plansContext.removeSelectedOrderIds(orderId);
    plansContext.removeOrderIds([orderId]);
    plansContext.addAllocatedOrderIds([orderId]);

    bulkDeleteMutate(
      [id],
      {
        onSuccess: () => {
          resetQuery();
          enqueueSnackbar(`1件の案件を削除しました`, { variant: 'success' });
        },
        onError: () => {
          plansContext.bulkAddSelectedOrderIds([orderId]);
          plansContext.addOrderIds([orderId]);
          plansContext.removeAllocatedOrderIds([orderId]);
          enqueueSnackbar('案件の削除でエラーが発生しました。', { variant: 'error' });
        },
        onSettled: () => {
          plansContext.setIsLoading(false);
        }
      }
    );
  }, [bulkDeleteMutate, resetQuery, shipmentOrderLegRunEntityMap]);

  const mutateCloneOrder: (shipmentOrderId: number) => void = useCallback((shipmentOrderId) => {
    plansContext.setIsLoading(true);

    try {
      cloneShipmentOrder.mutate(shipmentOrderId);
    } finally {
      plansContext.setIsLoading(false);
    }
  }, []);

  const resetDrivers = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['useQueryPlanningsDrivers'] });
    void queryClient.invalidateQueries({ queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY] });
  }, []);

  const mergeYoidumi = useCallback((deliveries: PlanningsDeliveryEntity[], responseData: PlanningsDeliveryEntity[]) => {
    const ids = deliveries.map((it) => it.id);
    const extraEntities = responseData.filter((it) => !ids.includes(it.id));
    return [...deliveries, ...extraEntities];
  }, []);

  const selectedOrderIdsRef = useRef(new Set(selectedOrderIds));
  useEffect(() => {
    selectedOrderIdsRef.current = new Set(selectedOrderIds);
  }, [selectedOrderIds]);

  /**
   * 割り当て済みの案件IDを取得し、コンテキストに反映する
   * 割り当て済みの案件を未割り当てリストから除外し、割り当て済みリストに追加する
   */
  const updateAllocatedOrderIds = useCallback(() => {
    const deliveryHasOrderIds: number[] = arrayUtil
      .uniq<number>(
        shipmentOrderLegRunsInfiniteData.pages.flatMap((page) => page.data).filter((it) => it.assigned).map((it) => it.id)
      );

    plansContext.setAllocatedOrderIds(deliveryHasOrderIds);
    if (deliveryHasOrderIds.some((it) => orderIds.includes(it))) {
      plansContext.removeOrderIds(deliveryHasOrderIds);
    }
    if (deliveryHasOrderIds.some((it) => selectedOrderIdsRef.current.has(it))) {
      plansContext.bulkRemoveSelectedOrderIds(deliveryHasOrderIds);
    }
  }, [shipmentOrderLegRunsInfiniteData, orderIds]);

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const mutateDeleteOrdersOperations: (requestOrderIds: number[]) => void = useCallback(async (requestOrderIds) => {
    enqueueSnackbar('配送計画を削除します');

    plansContext.setIsLoading(true);

    try {
      await operationBulkResetting.mutateAsync(requestOrderIds, {
        onSuccess: (res: AxiosResponse<PlanningsDeliveryEntity[]>) => {
          // 宵積みがあるとリクエストに含まれないものも削除するので、戻り値を受け取り追加する。
          const merged = mergeYoidumi(deliveryEntities, res.data);

          const allocateHistoryEntity: AllocateHistoryEntity = {
            version: generateRandomString(20),
            companyId: licenseContext.config?.selected_company_id,
            actAt: new Date(),
            action: '割当部分解除',
            message: `${requestOrderIds.length.toLocaleString()}件の割当をすべて解除しました`,
            deliveryEntities: merged,
          };

          plansContext.addAllocateHistoryEntity(allocateHistoryEntity);
        }
      });

      plansContext.addOrderIds(requestOrderIds);
      plansContext.removeAllocatedOrderIds(requestOrderIds);
      resetQuery();

      enqueueSnackbar('配送計画を削除しました');
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosError = error as unknown as AxiosError<{ message: string }>;
        enqueueSnackbar(axiosError.response?.data?.message || fiveZeroZeroErrorMessage);
      } else {
        enqueueSnackbar('リセット処理でエラーが発生しました。');
      }
    } finally {
      plansContext.setIsLoading(false);
    }
  }, [deliveryEntities, enqueueSnackbar, licenseContext.config?.selected_company_id, mergeYoidumi, reset, resetQuery]);

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const mutateDeleteShiftsOperations: (deliveryIds: number[]) => void = useCallback(async (deliveryIds) => {
    if (!window.confirm('選択した計画をリセットしますか？')) { return; }

    const requestOrderIds: number[] = [];
    selectedCycleIndexes.forEach((it) => {
      const delivery = planningsOperationDeliveryByDeliveryIdEntity[`${it.deliveryId}`];
      if (delivery) {
        it.cycleIndexes.forEach((idx) => {
          const cycle = delivery.cycles.find((cycl) => cycl.cycleIndex === idx);
          if (cycle) {
            requestOrderIds.push(...Array.from(new Set(cycle.allOperations().map((ops) => ops.orderId))));
          }
        });
      }
    });

    enqueueSnackbar(`${deliveryIds.length.toLocaleString()}件の配送計画を削除します`);

    plansContext.setIsLoading(true);

    try {
      await operationBulkResetting.mutateAsync(requestOrderIds, {
        onSuccess: (res: AxiosResponse<PlanningsDeliveryEntity[]>) => {
          // 宵積みがあるとリクエストに含まれないものも削除するので、戻り値を受け取り追加する。
          const merged = mergeYoidumi(deliveryEntities, res.data);

          const allocateHistoryEntity: AllocateHistoryEntity = {
            version: generateRandomString(20),
            companyId: licenseContext.config?.selected_company_id,
            actAt: new Date(),
            action: '割当全解除',
            message: `${res.data.length.toLocaleString()}件の割当をすべて解除しました`,
            deliveryEntities: merged,
          };

          plansContext.addAllocateHistoryEntity(allocateHistoryEntity);
          plansContext.addOrderIds(requestOrderIds);
          plansContext.removeAllocatedOrderIds(requestOrderIds);
          enqueueSnackbar(`${res.data.length.toLocaleString()}件の配送計画を削除しました`);
        },
      });

      const invalidateQueriesRequests = deliveryIds.map((id) => queryClient.invalidateQueries(['useQueryPlanningsDelivery', { id }]));
      await Promise.all(invalidateQueriesRequests);

      dispatchEditPlaces({ type: 'reset' });
      resetQuery();
      updateAllocatedOrderIds();
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosError = error as unknown as AxiosError<{ message: string }>;
        enqueueSnackbar(axiosError.response?.data?.message || fiveZeroZeroErrorMessage);
      } else {
        enqueueSnackbar('リセット処理でエラーが発生しました。');
      }
    } finally {
      plansContext.setIsLoading(false);
    }
  }, [deliveryEntities, enqueueSnackbar, licenseContext.config?.selected_company_id, mergeYoidumi, planningsOperationDeliveryByDeliveryIdEntity, queryClient, reset, resetQuery, selectedCycleIndexes, updateAllocatedOrderIds]);

  const { data: planningOrderStatisticsEntity } = useQueryPlanningsLegRunStatistics(startOn, endOn, selectedOrderIds);

  const { data: notAllocSuggestions } = useQueryPlanningsSuggestions(notAllocState.sessionId); // 未割り当て理由があればサジェストもある
  const { data: notAllocReasonData, isLoading: notAllocReasonDataIsLoading } = useQueryPlanningsNotAllocReasons(notAllocState.sessionId);

  const showNotAllocReasons = useCallback(() => {
    if (!notAllocState.sessionId) return;
    if (notAllocReasonDataIsLoading) return;
    if ([truckData, driverData, notAllocReasonData, deliveryData].some((maybe) => !maybe)) return;

    const truckOrder = truckData.reduce((map, truck, index) => { map[truck.id] = index; return map; }, {});
    const driverOrder = driverData.reduce((map, driver, index) => { map[driver.id] = index; return map; }, {});
    const notAllocDeliveryIds = notAllocReasonData.map((it) => it.delivery_id);
    const notAllocDeliveries = deliveryData.filter((it) => notAllocDeliveryIds.includes(it.id));
    const sorted = notAllocReasonData.sort((a, b) => {
      const deliveryA = notAllocDeliveries.find((it) => it.id === a.delivery_id);
      const deliveryB = notAllocDeliveries.find((it) => it.id === b.delivery_id);
      // 宵積みの場合に翌日の deliveryId が返ってくることがあるため、deliveryId がない場合は 0 として扱う
      if (deliveryA === undefined || deliveryB === undefined) return 0;

      let ret = truckOrder[deliveryA.truckId] - truckOrder[deliveryB.truckId];
      if (ret === 0) {
        ret = driverOrder[deliveryA.driverId] - driverOrder[deliveryB.driverId];
      }
      return ret;
    });
    if (notAllocState.disabledOrders.length > 0) {
      notAllocState.disabledOrders.forEach((it: number) => {
        sorted.push({
            order_id: it,
            delivery_id: 0,
            message: '有料道路を使用しないと到達できないルートがあります',
        });
      });
    }

    plansContext.setPlanningsNotAllocReasons(sorted);
  }, [deliveryData, driverData, notAllocReasonData, notAllocReasonDataIsLoading, notAllocState, truckData]);

  useEffect(() => {
    if (notAllocReasonDataIsLoading) return;

    showNotAllocReasons();
  }, [notAllocReasonDataIsLoading, showNotAllocReasons]);

  useEffect(() => {
    if (!shipmentOrderLegRunsInfiniteData) return;

    updateAllocatedOrderIds();
  }, [shipmentOrderLegRunsInfiniteData]);

  const handlePlanningResponse = useCallback((response: PlanningResponseV5Entity, message: string) => {
    response.version = 5;
    plansContext.handlePlanningResponse(response, message, resetQuery);
  }, []);

  const handleErrorResponse = useCallback((error: AxiosError<{
    message: string;
  }>) => {
    plansContext.handleErrorResponse(error, resetQuery);
    updateAllocatedOrderIds();
  }, [resetQuery, updateAllocatedOrderIds]);

  const requestSingleAlgorithmPlanning = useCallback((deliveryId: number, orderOperationIdsForSort: number[], deleteOrderIdsFromOperations: number[], orderOperationCycleIndexes?: { [key: number]: number }) => {
    const delivery = deliveryData.find((it) => it.id === deliveryId);
    const { shiftId } = delivery;

    const plan: PlanningEntity = {
      startOn,
      endOn,
      lockType: 'sequence',
      isRespectRecent: false,
      excludeToll: false,
      respectType: 'day_of_week',
      respectDaysOfWeek: [],
      respectOn: '',
      isTimeTableBackward: false,
      includeFutureDeliveries: true,
      activateConstraintLoadAfterNotCleared: false,
      orderOperationIdsForSort,
      deleteOrderIdsFromOperations,
      selectedRelaxedRules: [],
      priorityLargeTrucks: 0.5,
      expandWorkingStartTime: 0,
      expandWorkingEndTime: 0,
      expandLoadStartTime: 0,
      expandLoadEndTime: 0,
      expandUnloadStartTime: 0,
      expandUnloadEndTime: 0,
      expandMaxVolumeRate: 0.0,
      expandMaxLoadCapacityRate: 0.0,
      requestAsync: false,
      mlSourceType: -1,
      balancedLoading: false,
      orderOperationCycleIndexes,
      autoSplitOrders: false,
      dontExchangePreroute: true,
      concurrentAllOrNothing: false,
      loadAfterInitialStock: licenseContext.config.load_after_initial_stock,
      setCurrentTimeToAlgorithm: licenseContext.config.set_current_time_to_algorithm,
      mlPlanning: true,
      optimizeForLoad: false,
      forceNullBase: false,
      trunkTransportation: false,
      activateIncrementalMaxNumPistons: false,
      traceMode: false,
    };

    const truck = truckEntities.find(({ id }) => id === delivery.truckId);
    const driver = driverEntities.find(({ id }) => id === delivery.driverId);
    const message = `${driver.name}(${truck.licensePlateValue})の自動配車を編集しました`;

    const allocateHistoryEntity: AllocateHistoryEntity = {
      version: generateRandomString(20),
      companyId: licenseContext.config?.selected_company_id,
      actAt: new Date(),
      action: '編集',
      message,
      deliveryEntities,
    };

    let async = false;
    normalPlanning.mutate(
      {
        shiftIds: [shiftId],
        orderIds: [],
        planning: plan,
        latestAlgorithmRequestVersion,
        requestAsync: true,
        selectedCycleIndexes: [],
        deliveryOperations: [],
        planningSetting: null,
      },
      {
        onSuccess: (data: AxiosResponse<PlanningResponseV5Entity>) => {
          dispatchPlanning({
            type: 'resetRestrictedInsertPosition',
          });
          plansContext.addAllocateHistoryEntity(allocateHistoryEntity);
          if (data.data.status === 'queued') {
            async = true;
            const entity: AsyncPlanningResponseEntity = data.data;
            setAsyncPlanningResponse(entity);
            enqueueSnackbar('自動配車を実行中です。完了までしばらくお待ち下さい。');
          } else {
            handlePlanningResponse(data.data, '修正が完了しました');
          }
        },
        onError: (e: AxiosError<{ message: string; }>) => {
          handleErrorResponse(e);
        },
        onSettled: () => {
          if (async) return;

          plansContext.setIsLoading(false);
          plansContext.setCalculating(false);
        }
      }
    );
  }, [deliveryData, startOn, endOn, truckEntities, driverEntities, licenseContext.config, deliveryEntities, latestAlgorithmRequestVersion, enqueueSnackbar, handlePlanningResponse, handleErrorResponse]);

  const { rollbackRequest } = useRollbackRequest({
    currentHistoryVersion,
    resetCurrentHistoryVersion,
    resetQuery,
    deliveryData: null, // TODO: 実装する
  });

  const {
    planning,
    dispatchPlanning,
    selectedRelaxedRules,
    setSelectedRelaxedRules,
    setPriorityLargeTrucks,
    updateBalancedLoading,
    updateIsRespectRecent,
    updateExcludeToll,
    updateRespectType,
    updateRespectDaysOfWeek,
    updateRespectOn,
    updateIsTimeTableBackward,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateMLPlanning,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    updateMLSourceType,
    updateOptimizeForLoad,
    updateForceNullBase,
    updateTrunkTransportation,
    updateActivateIncrementalMaxNumPistons,
    updateTraceMode,
  } = usePlanningSettings({
    activateConstraintLoadAfterNotCleared,
    loadAfterInitialStock,
  });

  const {
    asyncPlanningResponse,
    setAsyncPlanningResponse,
    forcePlanningQueue,
    currentForcePlanning,
    assignTruckQueue,
    currentAssigning,
    suppressLoading,
    requestForcePlanning,
    assignTruckToTemporaryDeliveryRequest,
    resetQueue,
  } = usePlanningQueue({
    startOn,
    endOn,
    relaxedRules: selectedRelaxedRules,
    normalPlanning,
    assignTruckMutate: assignTruckToTermporaryDelivery.mutate,
    dispatchPlanning,
    handlePlanningResponse,
    handleErrorResponse,
  });
  useEffect(() => {
    resetQueueRef.current = resetQueue;
  }, [resetQueue]);

  const { planningRequest } = usePlanningRequest({
    dispatchPlanning,
    setAsyncPlanningResponse,
    generateRandomString,
    resetQuery,
    normalPlanningV5: normalPlanning,
  });

  const { requestScenarioPlanning } = useScenarioPlanningRequestV5({
    dispatchPlanning,
    setAsyncPlanningResponse,
    resetQuery,
  });

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const transferRequest: (requestDeliveryIds: number[], requestOrderIds: number[]) => void = useCallback(async (requestDeliveryIds, requestOrderIds) => {
    if (!requestDeliveryIds) return;
    if (!requestOrderIds) return;

    const transferToDelivery = deliveryEntities
      .find(({ id }) => id === requestDeliveryIds[0]);
    const transferToShiftId = transferToDelivery.shiftId;
    const transferFromDelivery = deliveryEntities
      .find((delivery) => requestOrderIds.some((orderId) => delivery.operations.map((it) => it.orderId).includes(orderId)));
    const transferFromShiftIds = [
      transferFromDelivery.shiftId
    ];

    if (!transferToShiftId) return;
    if (!transferFromShiftIds.length) return;

    const trucks = [transferToDelivery, transferFromDelivery]
      .map(({ truckId }) => truckEntities.find(({ id }) => id === truckId));
    const message = `${trucks.map(({ licensePlateValue }) => licensePlateValue).join('から')}へ${requestOrderIds.length.toLocaleString()}件の配送計画を移管しました`;

    const allocateHistoryEntity: AllocateHistoryEntity = {
      version: generateRandomString(20),
      companyId: licenseContext.config?.selected_company_id,
      actAt: new Date(),
      action: '割当移管',
      message,
      deliveryEntities,
    };

    plansContext.addAllocateHistoryEntity(allocateHistoryEntity);

    enqueueSnackbar(`${requestOrderIds.length.toLocaleString()}件の配送計画を移動します`);

    plansContext.setCalculating(true);
    plansContext.setIsLoading(true);

    let resetSuccess = true;
    await operationBulkResetting.mutateAsync(requestOrderIds).catch((error: AxiosError<{ message :string }>) => {
      enqueueSnackbar(error.response?.data?.message || fiveZeroZeroErrorMessage);
      resetSuccess = false;
    });
    if (!resetSuccess) {
      plansContext.setCalculating(false);
      plansContext.setIsLoading(false);
      return;
    }

    let async = false;
    transferPlanning.mutate({
      orderIds: requestOrderIds,
      transferToShiftId,
      planning,
      latestAlgorithmRequestVersion,
      transferFromShiftId: transferFromDelivery.shiftId,
    }, {
      onSuccess: (data: AxiosResponse<PlanningResponseV5Entity>) => {
        if (data.data.status === 'queued') {
          async = true;
          const entity: AsyncPlanningResponseEntity = data.data;
          setAsyncPlanningResponse(entity);
          enqueueSnackbar('自動配車を実行中です。完了までしばらくお待ち下さい。');
        } else {
          handlePlanningResponse(data.data, `${requestOrderIds.length.toLocaleString()}件の配送計画を移動しました`);
        }
      },
      onError: (e: AxiosError<{ message: string; }>) => {
        handleErrorResponse(e);
      },
      onSettled: () => {
        if (async) return;

        plansContext.setIsLoading(false);
        plansContext.setCalculating(false);
      }
    });
  }, [deliveryEntities, handleErrorResponse, handlePlanningResponse, latestAlgorithmRequestVersion, licenseContext.config?.selected_company_id, operationBulkResetting, planning, transferPlanning, truckEntities]);

  const swapRequest: (swapFromDeliveryId: number, swapToDeliveryId: number) => void = useCallback((swapFromDeliveryId, swapToDeliveryId) => {
    const requestDeliveryIds: [number, number] = [swapFromDeliveryId, swapToDeliveryId];

    const deliveries = [swapFromDeliveryId, swapToDeliveryId].map((it) => deliveryEntities.find(({ id }) => id === it));
    const haveYoizumi = deliveries.map((it) => !!(it.operations.length % 2)).some((bool) => bool);
    const includesFixed = deliveries.some((it) => it.operations.some((op) => op.fixed));

    if (haveYoizumi) {
      enqueueSnackbar('宵積みが存在しているため、配送計画の入れ替えはできません');
      return;
    }
    if (includesFixed) {
      enqueueSnackbar('確定した計画のため、配送計画の入れ替えはできません');
      return;
    }
    if (!window.confirm('選択した配送計画を入れ替えますか？')) { return; }

    const trucks = deliveries
      .map(({ truckId }) => truckEntities.find(({ id }) => id === truckId));
    const message = `${trucks.map(({ licensePlateValue }) => licensePlateValue).join('と')}の配送計画を入れ替えました`;

    const allocateHistoryEntity: AllocateHistoryEntity = {
      version: generateRandomString(20),
      companyId: licenseContext.config?.selected_company_id,
      actAt: new Date(),
      action: '割当交換',
      message,
      deliveryEntities,
    };

    plansContext.addAllocateHistoryEntity(allocateHistoryEntity);

    enqueueSnackbar('配送計画を入れ替えします');
    plansContext.setIsLoading(true);
    plansContext.setCalculating(true);

    let async = false;
    swapPlanning.mutate({
        deliveryIds: requestDeliveryIds,
        latestAlgorithmRequestVersion,
        startOn,
        endOn,
    }, {
      onSuccess: (data: AxiosResponse<PlanningResponseEntity>) => {
        if (data.data.status === 'queued') {
          async = true;
          const entity: AsyncPlanningResponseEntity = data.data;
          setAsyncPlanningResponse(entity);
          enqueueSnackbar('自動配車を実行中です。完了までしばらくお待ち下さい。');
        } else {
          resetQuery();
          enqueueSnackbar('配送計画を入れ替えました');

          if (data.data.not_allocated_order_ids.length) {
            plansContext.addOrderIds(data.data.not_allocated_order_ids);
            plansContext.removeAllocatedOrderIds(data.data.not_allocated_order_ids);

            enqueueSnackbar(`${data.data.not_allocated_order_ids.length.toLocaleString()}件が割り当たりません`);
          }
        }
      },
      onError: (e: AxiosError<{ message: string; }>) => {
        handleErrorResponse(e);
      },
      onSettled: () => {
        if (async) return;
        plansContext.setIsLoading(false);
        plansContext.setCalculating(false);
      }
    });
  }, [licenseContext.config?.selected_company_id, deliveryEntities, enqueueSnackbar, latestAlgorithmRequestVersion, startOn, endOn, truckEntities, resetQuery, handleErrorResponse]);


  const [transfer, dispatchTransfer] = useReducer(transferRequestReducer, undefined);
  const setTransfer = useCallback(
    (entity: TransferRequestEntity) => {
      dispatchTransfer({
        type: 'set',
        payload: entity
      });
    },
    [],
  );
  const resetTransfer = useCallback(
    () => {
      dispatchTransfer({
        type: 'reset'
      });
    },
    [],
  );

  useEffect(() => {
    if (!deliveriesInfiniteData || deliveriesIsFetching || deliveriesIsLoading) {
      return;
    }
    setDeliveryData(deliveriesInfiniteData.pages.flatMap((page) => page.data));
    if (deliveriesHasNextPage) {
      void fetchNextDeliveries();
    }
  }, [deliveriesHasNextPage, deliveriesIsFetching, deliveriesIsLoading, deliveriesInfiniteData, fetchNextDeliveries]);

  useEffect(() => {
    const deliveryIds = deliveryData.map((it) => it.id);
    const indexes = selectedCycleIndexes.filter((it) => !deliveryIds.includes(it.deliveryId));
    indexes.forEach((it) => {
      plansContext.removeCycleIndex(it.deliveryId);
    });
  }, [deliveryData, selectedCycleIndexes]);

  useEffect(() => {
    if (!shipmentOrderLegRunsIsLoading && !shipmentOrderLegRunsIsFetching && shipmentOrderLegRunsHasNextPage) {
      void fetchNextShipmentOrderLegRuns();
    }
  }, [fetchNextShipmentOrderLegRuns, shipmentOrderLegRunsHasNextPage, shipmentOrderLegRunsIsFetching, shipmentOrderLegRunsIsLoading]);

  useEffect(() => {
    if (!shipmentOrderLegRunsInfiniteData || calculating) return;

    const entities = shipmentOrderLegRunsInfiniteData.pages.flatMap((page) => page.data);
    const map = new Map<number, ShipmentOrderLegRunForPlanEntity>();
    entities.forEach((it) => {
      map.set(it.id, it);
    });
    dispatchShipmentOrderLegRunEntityMap({
      type: 'set',
      payload: map
    });

  }, [calculating, shipmentOrderLegRunsInfiniteData]);

  useEffect(() => {
    if (!shipmentOrderLegRunEntityMap) return;

    const orders = Array.from(shipmentOrderLegRunEntityMap.values());
    if (orders.length === 0) return;
    const allOrderIds = orders.map((it) => it.id);
    plansContext.setOrderIds(allOrderIds.filter((it) => !allocatedOrderIds.includes(it)));
  }, [shipmentOrderLegRunEntityMap, allocatedOrderIds]);

  useEffect(() => {
    selectedOrderIds.forEach((it) => {
      if (!orderIds.includes(it) || allocatedOrderIds.includes(it)) {
        plansContext.removeSelectedOrderIds(it);
      }
    });
  }, [allocatedOrderIds, orderIds, selectedOrderIds]);

  const [importFile, dispatchImportFile] = useReducer(importFileReducer, undefined);
  const setImportFile = useCallback((file: File) => {
    dispatchImportFile({
      type: 'set',
      payload: file
    });
  }, []);

  const [importForUpdateFile, dispatchImportForUpdateFile] = useReducer(importForUpdateFileReducer, undefined);
  const setImportForUpdateFile = useCallback((file: File) => {
    dispatchImportForUpdateFile({
      type: 'set',
      payload: file
    });
  }, []);

  const { cloneLastWeekShifts } = useMutationShiftClone();

  const mutateCloneLastWeekShifts: (date: string) => void = useCallback((date: string) => {
    const startAt = new Date(startOn);
    const weekStartAt = format(startOfWeek(startAt), 'MM/dd', { timeZone: 'Asia/Tokyo' });
    const weekEndAt = format(endOfWeek(startAt), 'MM/dd', { timeZone: 'Asia/Tokyo' });

    const message = `・${weekStartAt} 〜 ${weekEndAt}間で作成済みのシフトは上書きされます。
・${weekStartAt} 〜 ${weekEndAt}間で作成済みの配車計画は削除されます。

よろしいですか？`;

    if (!confirm(message)) return;

    plansContext.setIsLoading(true);

    cloneLastWeekShifts.mutate(date, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['driverShifts'] });
        void queryClient.invalidateQueries({ queryKey: ['shifts'] });
      },
      onError: (e: AxiosError<{ message: string; }>) => {
        handleErrorResponse(e);
      },
      onSettled: () => {
        plansContext.setIsLoading(false);
      }
    });
  }, [handleErrorResponse, startOn]);

  const { operationSendMail } = useMutationPlanningOperations();

  const sendOperationMail = useCallback(() => {
    operationSendMail.mutate({ startOn, endOn, shiftTruckIds: selectedCycleIndexes.map((it) => it.deliveryId) }, {
      onSuccess: () => { enqueueSnackbar('配送指示メールを送信しました'); },
      onError: (e: AxiosError<{ message: string; }>) => {
        const errMsg = e.response.data.message;
        enqueueSnackbar(`配送指示メールの送信がエラーになりました。${errMsg}`);
      }
    });
  }, [endOn, enqueueSnackbar, selectedCycleIndexes, startOn]);

  const handleConfirmDeliveries = useCallback((deliveryIds: number[]) => {
    confirmMutation.mutate({
      shift_truck_ids: deliveryIds,
      fixed: true
    }, {
      onSuccess: (_result) => {
        enqueueSnackbar(`${deliveryIds.length}件の配車を確定しました`, { variant: 'success' });
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          const axiosError = error as AxiosError<{ message: string }>;
          enqueueSnackbar(axiosError.response?.data?.message || '配車確定に失敗しました', { variant: 'error' });
        } else {
          enqueueSnackbar('配車確定に失敗しました', { variant: 'error' });
        }
      }
    });
  }, [confirmMutation, enqueueSnackbar]);

  const handleUnconfirmDeliveries = useCallback((deliveryIds: number[]) => {
    confirmMutation.mutate({
      shift_truck_ids: deliveryIds,
      fixed: false
    }, {
      onSuccess: (_result) => {
        enqueueSnackbar(`${deliveryIds.length}件の配車確定を解除しました`, { variant: 'success' });
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          const axiosError = error as AxiosError<{ message: string }>;
          enqueueSnackbar(axiosError.response?.data?.message || '配車確定解除に失敗しました', { variant: 'error' });
        } else {
          enqueueSnackbar('配車確定解除に失敗しました', { variant: 'error' });
        }
      },
    });
  }, [confirmMutation, enqueueSnackbar]);

  const refreshOperationStatuses = useCallback(() => {
    resetQuery();
    void queryClient.invalidateQueries({ queryKey: ['lastOperationStatusUpdated']});
  }, [queryClient, resetQuery]);

  useEffect(() => {
    if ([shipmentOrderLegRunsIsInitialLoading, trucksIsLoading, driversIsLoading, deliveriesIsInitialLoading].some((bool) => bool)) {
      plansContext.setIsLoading(true);
    } else {
      plansContext.setIsLoading(false);
    }
  }, [shipmentOrderLegRunsIsInitialLoading, trucksIsLoading, driversIsLoading, deliveriesIsInitialLoading]);

  useEffect(() => {
    const { allocateHistories } = plansContext;
    if (!allocateHistories) return;
    if (!allocateHistories.length) return;

    setLatestAllocateHistory(
      allocateHistories[0]
    );
  }, [plansContext]);

  useEffect(() => {
    if (!importFile) return;

    const requestPath = '/api/v2/orders/import';

    const data = new FormData();
    data.append('file', importFile);

    plansContext.setIsLoading(true);
    enqueueSnackbar('インポートを開始します');

    axios
      .post(requestPath, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(
        (response: AxiosResponse<{ severity: string; message: string }>) => {
          enqueueSnackbar(response.data.message);
        }
      )
      .catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.response.status === 400) {
          enqueueSnackbar(
            '取り込めない注文がありました、データをご確認ください。'
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
          const blob = new Blob([e.response.data], {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
            type: e.response.data.type
          });
          saveAs(blob, 'errors.txt');
        } else {
          enqueueSnackbar(fiveZeroZeroErrorMessage);
        }
      })
      .finally(() => {
        reset();
        plansContext.setIsLoading(false);
      });
  }, [importFile]);

  useEffect(() => {
    if (!importForUpdateFile) return;

    const requestPath = '/api/v3/orders/import_for_update';

    const data = new FormData();
    data.append('file', importForUpdateFile);

    plansContext.setIsLoading(true);
    enqueueSnackbar('インポートを開始します');

    axios.post(requestPath, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(
      (response: AxiosResponse<{ severity: string; message: string }>) => {
        enqueueSnackbar(response.data.message);
      }
    ).catch((e) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (e.response.status === 400) {
        enqueueSnackbar(
          '取り込めない注文がありました、データをご確認ください。'
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
        const blob = new Blob([e.response.data], {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
          type: e.response.data.type
        });
        saveAs(blob, 'errors.txt');
      } else {
        enqueueSnackbar(fiveZeroZeroErrorMessage);
      }
    })
    .finally(() => {
      reset();
      setImportForUpdateFile(undefined);
      plansContext.setIsLoading(false);
    });
  }, [enqueueSnackbar, importForUpdateFile, reset, setImportForUpdateFile]);

  useEffect(() => {
    plansContext.setTruckEntities(truckData || []);
  }, [truckData]);

  useEffect(() => {
    plansContext.setDriverEntities(driverData || []);
  }, [driverData]);

  useEffect(() => {
    plansContext.setDeliveryEntities(convertShipmentOrderLegRunEntitiesToDeliveries(deliveryData, shipmentOrderLegRunEntityMap) || []);
  }, [deliveryData, shipmentOrderLegRunEntityMap]);

  useEffect(() => {
    if (!deliveryEntities) {
      dispatchOperationEntities({ type: 'reset' });
    } else {
      dispatchOperationEntities({
        type: 'set',
        payload: deliveryEntities.flatMap(({ operations }) => operations)
      });
    }
  }, [deliveryEntities]);

  useEffect(() => {
    dispatchLastOperationStatusUpdated({
      type: 'set',
      payload: lastOperationStatusUpdatedData
    });
  }, [lastOperationStatusUpdatedData]);

  useEffect(() => {
    plansContext.setLatestAlgorithmRequestVersion(latestAlgorithmRequestVersionData);
  }, [latestAlgorithmRequestVersionData]);


  useEffect(() => {
    localStorage.setItem('planningOn', startOn);
  }, [startOn]);

  const { delete: deleteDelivery } = useMutationPlanningDelivery();
  const deleteTemporaryDelivery = useCallback((deliveryId: number) => {
    const deliveryToDelete = deliveryData.find((it) => it.id === deliveryId);
    if (!deliveryToDelete) {
      return;
    }

    const delivery: PlanningsDeliveryEntity = {
      ...deliveryToDelete,
      orderAssignments: [{ id: 0, order_id: 0, }], // 使わないので適当な値をセット
      operations: [], // 使わないので適当な値をセット
    };

    deleteDelivery.mutateAsync(delivery).then(() => {
      plansContext.addOrderIds(deliveryToDelete.operations.map((it) => it.shipmentOrderLegRunId));
      plansContext.removeAllocatedOrderIds(deliveryToDelete.operations.map((it) => it.shipmentOrderLegRunId));
    }).catch((error: AxiosError<{ message: string }>) => {
      enqueueSnackbar(error.response.data.message, { variant: 'error' });
    });
    updateSelectedCycleIndexes(deliveryId, []);
  }, [endOn, startOn, updateSelectedCycleIndexes, deliveryData]);

  const tariffCalculationMutation = useTariffCalculationMutation();

  const handleTariffCalculationExecute = useCallback((params: TariffCalculationParams, onSuccess?: () => void) => {
    tariffCalculationMutation.mutate(params, {
      onSuccess: (data) => {
        const messages: string[] = [];
        if (data.billingUpdatedCount > 0) {
          messages.push(`請求: ${data.billingUpdatedCount}件`);
        }
        if (data.paymentUpdatedCount > 0) {
          messages.push(`支払: ${data.paymentUpdatedCount}件`);
        }
        if (data.skippedCount > 0) {
          messages.push(`スキップ: ${data.skippedCount}件`);
        }
        enqueueSnackbar(`運賃計算が完了しました。${messages.join('、')}`, { variant: 'success' });
        void queryClient.invalidateQueries({
          queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY]
        });
        onSuccess?.();
      },
      onError: (error: unknown) => {
        const axiosError = error as { response?: { data?: { message?: string } } };
        const message = axiosError?.response?.data?.message || '運賃計算に失敗しました。';
        enqueueSnackbar(message, { variant: 'error' });
      },
    });
  }, [tariffCalculationMutation, enqueueSnackbar]);

  return (
    <Presenter
      planningRequest={planningRequest}
      startOn={startOn}
      endOn={endOn}
      orderIds={orderIds}
      selectedOrderIds={selectedOrderIds}
      addSelectedOrderId={addSelectedOrderId}
      removeSelectedOrderId={removeSelectedOrderId}
      unit={unit}
      isLoading={isLoading}
      updateBalancedLoading={updateBalancedLoading}
      updateIsRespectRecent={updateIsRespectRecent}
      updateExcludeToll={updateExcludeToll}
      updateRespectType={updateRespectType}
      updateRespectDaysOfWeek={updateRespectDaysOfWeek}
      updateRespectOn={updateRespectOn}
      mutateDeleteShiftsOperations={mutateDeleteShiftsOperations}
      mutateDeleteOrdersOperations={mutateDeleteOrdersOperations}
      transferRequest={transferRequest}
      transfer={transfer}
      setTransfer={setTransfer}
      resetTransfer={resetTransfer}
      mutateDeleteOrder={mutateDeleteOrder}
      mutateDeleteSpecificOrder={mutateDeleteSpecificOrder}
      mutateCloneOrder={mutateCloneOrder}
      setImportFile={setImportFile}
      setImportForUpdateFile={setImportForUpdateFile}
      mutateCloneLastWeekShifts={mutateCloneLastWeekShifts}
      displayOrderId={displayOrderId}
      resetDisplayOrderId={resetDisplayOrderId}
      updateDisplayOrderId={updateDisplayOrderId}
      planningMapUnallocatedOrderPositionEntities={planningMapUnallocatedOrderPositionEntities}
      garageData={garageData}
      truckEntities={truckEntities}
      driverEntities={driverEntities}
      deliveryEntities={deliveryEntities}
      operationEntities={operationEntities}
      shipmentOrderLegRunEntityMap={shipmentOrderLegRunEntityMap}
      swapRequest={swapRequest}
      planningOrderStatisticsEntity={planningOrderStatisticsEntity}
      groupEntities={groupEntities}
      planningsMapGaragePositionEntities={planningsMapGaragePositionEntities}
      planningsMapOperationPositionEntities={planningsMapOperationPositionEntities}
      planningsOperationEntitiesWithStatsByDeliveryIdEntity={planningsOperationEntitiesWithStatsByDeliveryIdEntity}
      updateIsTimeTableBackward={updateIsTimeTableBackward}
      sendOperationMail={sendOperationMail}
      updateIncludeFutureDeliveries={updateIncludeFutureDeliveries}
      updateAutoSplitOrders={updateAutoSplitOrders}
      updateDontExchangePreroute={updateDontExchangePreroute}
      updateConcurrentAllOrNothing={updateConcurrentAllOrNothing}
      updateMLPlanning={updateMLPlanning}
      refreshOperationStatuses={refreshOperationStatuses}
      lastOperationStatusUpdated={lastOperationStatusUpdated}
      resetDrivers={resetDrivers}
      flyToPosition={flyToPosition}
      resetFlyToPosition={resetFlyToPosition}
      fitBoundsPositions={fitBoundsPositions}
      resetFitBoundsPositions={resetFitBoundsPositions}
      currentHistoryVersion={currentHistoryVersion}
      setCurrentHistoryVersion={setCurrentHistoryVersion}
      resetCurrentHistoryVersion={resetCurrentHistoryVersion}
      resetAllocateHistories={resetAllocateHistories}
      rollbackRequest={rollbackRequest}
      customInputFields={customInputFields}
      relaxedRules={relaxedRules}
      selectedRelaxedRules={selectedRelaxedRules}
      setSelectedRelaxedRules={setSelectedRelaxedRules}
      setPriorityLargeTrucks={setPriorityLargeTrucks}
      priorityLargeTrucks={planning.priorityLargeTrucks}
      updateExpandWorkingStartMinutes={updateExpandWorkingStartMinutes}
      updateExpandWorkingEndMinutes={updateExpandWorkingEndMinutes}
      updateExpandLoadStartMinutes={updateExpandLoadStartMinutes}
      updateExpandLoadEndMinutes={updateExpandLoadEndMinutes}
      updateExpandUnloadStartMinutes={updateExpandUnloadStartMinutes}
      updateExpandUnloadEndMinutes={updateExpandUnloadEndMinutes}
      updateExpandMaxVolumeRate={updateExpandMaxVolumeRate}
      updateExpandMaxLoadCapacityRate={updateExpandMaxLoadCapacityRate}
      pastOperationsData={pastOperationsData}
      latestAllocateHistory={latestAllocateHistory}
      directionSettingDialogIsOpen={directionSettingDialogIsOpen}
      updateDirectionSettingDialogIsOpen={updateDirectionSettingDialogIsOpen}
      planningRunningEntities={planningRunningEntities}
      refreshRunningEntities={refreshRunningEntities}
      cancelRunningPlan={cancelRunningPlan}
      updateMLSourceType={updateMLSourceType}
      requestSingleAlgorithmPlanning={requestSingleAlgorithmPlanning}
      selectedCycleIndexes={selectedCycleIndexes}
      updateSelectedCycleIndexes={updateSelectedCycleIndexes}
      planningsOperationDeliveryByDeliveryIdEntity={planningsOperationDeliveryByDeliveryIdEntity}
      editPlaces={editPlaces}
      resetEditPlaces={resetEditPlaces}
      updateEditPlaces={updateEditPlaces}
      addEmptyCycle={addEmptyCycle}
      removeEmptyCycle={removeEmptyCycle}
      requestScenarioPlanning={requestScenarioPlanning}
      asyncPlanningResponse={asyncPlanningResponse}
      mutateRestoreSplittedOrder={mutateRestoreSplittedOrder}
      mutateRestoreSplittedOrders={mutateRestoreSplittedOrders}
      updateOptimizeForLoad={updateOptimizeForLoad}
      updateForceNullBase={updateForceNullBase}
      updateTrunkTransportation={updateTrunkTransportation}
      currentView={view}
      updateView={updateView}
      requestForcePlanning={requestForcePlanning}
      resetQuery={resetQuery}
      forcePlanningQueue={forcePlanningQueue}
      currentForcePlanning={currentForcePlanning}
      assignTruckToTemporaryDeliveryRequest={assignTruckToTemporaryDeliveryRequest}
      assignTruckQueue={assignTruckQueue}
      currentAssigning={currentAssigning}
      allDeliveriesLoaded={allDeliveriesLoaded}
      partnerCompanyEntities={partnerCompanyEntities}
      ref={childRef}
      deleteTemporaryDelivery={deleteTemporaryDelivery}
      afterResetOrders={afterResetOrders}
      notAllocSuggestions={notAllocSuggestions}
      handleConfirmDeliveries={handleConfirmDeliveries}
      handleUnconfirmDeliveries={handleUnconfirmDeliveries}
      isConfirming={confirmMutation.isPending}
      isUnconfirming={confirmMutation.isPending}
      updateActivateIncrementalMaxNumPistons={updateActivateIncrementalMaxNumPistons}
      updateTraceMode={updateTraceMode}
      showLegRunLoading={shipmentOrderLegRunsIsLoading || shipmentOrderLegRunsIsFetching || shipmentOrderLegRunsHasNextPage}
      handleTariffCalculationExecute={handleTariffCalculationExecute}
      tariffCalculationIsExecuting={tariffCalculationMutation.isPending}
    />
  );
});

export default Component;
